import { useRepositoryMutation, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt, NumberService, showSuccessAlert, useCurrencyCultureInfo } from '@mews/framework';
import { selectCashierData } from '../../../../settings/queries/useCashierData';
import { TransactionType } from '../../../types';
import {
    getCashierTransactionFormDefaultValues,
    CashierTransactionFormValues,
    getCashierTransactionValidationSchema,
} from './form/formMapping';
import { CashierTransactionFormContent } from './form/CashierTransactionFormContent';

interface Params {
    cashierId: string;
}

const CreateCashierTransactionForm = ({ cashierId, ...props }: Params & CommanderFormProps) => {
    const { mutateAsync } = useRepositoryMutation('cashiers', 'addTransaction', {
        invalidateQueries: {
            cashiers: ['getShiftTransactions', 'getShift', 'getCurrentShift', 'getCurrentShiftTransactions'],
        },
        onSuccess: () => {
            showSuccessAlert({
                description: lt('CashierTransactionAddSuccessMessage'),
            });
        },
    });

    const { data } = useRepositorySuspenseQuery(
        'cashiers',
        'getAll',
        { cashierIds: [cashierId] },
        { select: selectCashierData }
    );
    const singleCashier = data[0];
    const enabledCurrencies = singleCashier?.enabledCurrencies?.map((it) => it.code || '') ?? [];
    const localizationInfo = useCurrencyCultureInfo();

    const onSubmit = async (values: CashierTransactionFormValues) => {
        await mutateAsync({
            cashierId,
            currency: values.currency,
            value: NumberService.unformat(values.value, localizationInfo),
            isReceive: values.type === TransactionType.Received,
            notes: values.notes,
        });
    };

    const { renderFormContent } = useHandledDialogFormControls<CashierTransactionFormValues>({
        ...props,
        resolver: createZodFormResolver(getCashierTransactionValidationSchema(enabledCurrencies, localizationInfo)),
        onSubmit,
        defaultValues: getCashierTransactionFormDefaultValues(enabledCurrencies, localizationInfo),
    });

    return renderFormContent(<CashierTransactionFormContent enabledCurrencies={enabledCurrencies} />);
};

export const useCreateCashierTransactionDialog = (cashierId: string) =>
    useDialogForm({
        id: 'cashier-transaction-create-dialog',
        title: lt('CashierTransaction'),
        saveLabel: lt('Save'),
        renderForm: (props) => <CreateCashierTransactionForm cashierId={cashierId} {...props} />,
    });
